import { Div, Flex, Text } from '@sendoutcards/quantum-design-ui'
import React from 'react'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import { Dialog } from 'src/design_system/molecules/dialog/Dialog'
import {
  useDeviceInfo,
  useSelector,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'src/hooks'
import { LinkCopiedToaster } from './components/LinkCopiedToaster'
import { ThreeDayTrialModal } from './components/ThreeDayTrialModal'
import {
  CardFragment,
  DigitalCardOrderDataItem,
  DigitalCardRecipientGiftCardRunaFragment,
  ShareStatusType,
} from 'src/graphql/generated/graphql'
import { AnimatePresence } from 'framer-motion'
import {
  useDigitalGiftCardProducts,
  useGetDigitalCardByToken,
  useMarketingContent,
  useUpdateDigitalCardStatus,
} from 'src/react_query'
import { getCreateActSharePayload } from 'src/utils/getCreateActSharePayload'
import {
  GiftCardTile,
  GiftCardTileProps,
} from '../RecipientView/components/GiftCardTile'

type ShareScreenProps = {
  recipientName: string
  card?: CardFragment
  baseURL: string
  token: string
  onCopyToClipboard?: () => boolean | Promise<boolean>
  onFinish: () => void
  isSparse: boolean
  giftCard?: DigitalCardOrderDataItem
}

const RECIPIENT_SHARE_STATUS = {
  Created: 'CREATED',
  Shared: 'SHARED',
} as const

type LastAction = 'none' | 'copy' | 'share'

const formatAmount = (amount: string | number | null | undefined): string =>
  amount != null ? String(amount) : '0'

export const ShareScreen = (props: ShareScreenProps) => {
  const {
    recipientName,
    card,
    baseURL,
    token,
    onCopyToClipboard,
    onFinish,
    isSparse,
    giftCard,
  } = props

  const [hasCopyAffordance, setHasCopyAffordance] = useState(false)
  const [lastAction, setLastAction] = useState<LastAction>('none')

  const { data: marketingContent } = useMarketingContent({ suspense: true })
  const { mutateAsync: updateStatus } = useUpdateDigitalCardStatus()
  const { data: digitalCard } = useGetDigitalCardByToken({ token })
  const deviceInfo = useDeviceInfo()

  const isMobile = useSelector(state => state.window.width <= 760)

  const fontFamily = 'Montserrat, sans-serif'
  const boxShadow = '0px 0px 32px 0px rgba(0, 0, 0, 0.08)'
  const isFinalStep = lastAction === 'share'
  const link = useMemo(() => `${baseURL}/act/${token}`, [baseURL, token])
  const defaultActShareMessage = giftCard
    ? (marketingContent?.defaultActGiftCardDefaultMessage?.content ?? '')
    : (marketingContent?.defaultActShareMessage?.content ?? '')

  const shareData = useMemo(
    () => getCreateActSharePayload(deviceInfo.os, link, defaultActShareMessage),
    [deviceInfo.os, link, defaultActShareMessage],
  )

  const status = useMemo(() => {
    return digitalCard?.recipients.find(rec => rec.token === token)?.shareStatus
  }, [digitalCard?.recipients, token])

  const isCreatedStatus = status === RECIPIENT_SHARE_STATUS.Created

  const normalizedRecipient = (recipientName ?? '').trim()
  const recipientPretty =
    normalizedRecipient.length > 0
      ? normalizedRecipient.charAt(0).toUpperCase() +
        normalizedRecipient.slice(1)
      : 'your recipient'

  const getCardDimensions = useCallback(
    (mobile: boolean, isHorizontal?: boolean) => {
      if (!mobile) {
        return isHorizontal
          ? { width: '420px', height: '300px' }
          : { width: '300px', height: '420px' }
      }
      return isHorizontal
        ? { width: '231px', height: '160px' }
        : { width: '160px', height: '231px' }
    },
    [],
  )

  const { width, height } = useMemo(
    () => getCardDimensions(isMobile, card?.isHorizontal),
    [getCardDimensions, isMobile, card?.isHorizontal],
  )

  const backgroundImageCardStyles: React.CSSProperties = useMemo(
    () => ({
      backgroundImage: `url(${card?.frontPreviewUrl})`,
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      boxShadow,
      maxWidth: card?.isHorizontal ? '700px' : '500px',
      maxHeight: card?.isHorizontal ? '500px' : '700px',
      width: '100%',
      height: '100%',
      aspectRatio: card?.isHorizontal ? '7/5' : '5/7',
      zIndex: 1,
      margin: '60px 30px auto',
      borderRadius: '2px',
    }),
    [card?.frontPreviewUrl, card?.isHorizontal],
  )

  const nameStyles: React.CSSProperties = useMemo(
    () => ({
      color: '#000',
      fontWeight: 700,
      fontSize: isMobile ? '14px' : '16px',
      fontFamily,
    }),
    [isMobile],
  )

  const actionStyles: React.CSSProperties = useMemo(
    () => ({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      borderRadius: '12px',
      boxShadow,
      flexDirection: 'row',
      columnGap: '6px',
      fontFamily,
      fontSize: '14px',
      fontWeight: 600,
      padding: '10px 0',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'transform 120ms ease',
    }),
    [],
  )

  const headerTitle = useMemo(() => {
    switch (lastAction) {
      case 'copy':
        return 'Link copied'
      case 'share':
        return 'Your ACT Was Sent Successfully'
      default:
        return giftCard
          ? marketingContent?.shareScreenWGiftCardTitle.content
          : marketingContent?.shareScreenNonGiftCardTitle.content
    }
  }, [lastAction, marketingContent]) // eslint-disable-line

  const headerSubtitle = useMemo(() => {
    switch (lastAction) {
      case 'copy':
        return (
          <>
            Paste the link into your favorite app and send to{' '}
            <strong style={{ ...nameStyles }}>{recipientPretty}</strong>.
          </>
        )
      case 'share':
        return (
          <>
            Great job sending your ACT to{' '}
            <strong style={{ ...nameStyles }}>{recipientPretty}</strong>, It is
            sure to brighten their day.
          </>
        )
      default:
        return (
          <>
            Now its time to send it to{' '}
            <strong style={{ ...nameStyles }}>{recipientPretty}</strong>, use
            your favorite sharing service and send to your recipient.
          </>
        )
    }
  }, [lastAction, nameStyles, recipientPretty])

  const markCopySuccess = useCallback(async () => {
    setHasCopyAffordance(true)
    setLastAction('copy')
    try {
      await updateStatus({ status: ShareStatusType.Shared, token })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to update share status (copy)', err)
    }
  }, [token, updateStatus])

  const markShareSuccess = useCallback(async () => {
    setLastAction('share')
    try {
      await updateStatus({ status: ShareStatusType.Shared, token })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to update share status (share)', err)
    }
  }, [token, updateStatus])

  useEffect(() => {
    if (!hasCopyAffordance) return
    const t = setTimeout(() => {
      setHasCopyAffordance(false)
    }, 3000)
    return () => clearTimeout(t)
  }, [hasCopyAffordance])

  const handleCopyToClipboard = useCallback(async () => {
    let isCopySuccessful = false

    if (onCopyToClipboard) {
      isCopySuccessful = await Promise.resolve(onCopyToClipboard())
    } else if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard?.writeText
    ) {
      try {
        await navigator.clipboard.writeText(link)
        isCopySuccessful = true
      } catch {
        isCopySuccessful = false
      }
    }

    if (isCopySuccessful) {
      await markCopySuccess()
    }
  }, [link, onCopyToClipboard, markCopySuccess])

  const canWebShare = useMemo(() => {
    if (typeof navigator === 'undefined') return false
    const nav = navigator
    try {
      return typeof nav.canShare === 'function' && nav.canShare(shareData)
    } catch {
      return false
    }
  }, [shareData])

  const handleShare = useCallback(async () => {
    setLastAction('share')
    if (typeof navigator !== 'undefined') {
      const nav = navigator
      if (canWebShare && typeof nav.share === 'function') {
        try {
          await nav.share(shareData)
          await markShareSuccess()
        } catch {}
        return
      }
    }
  }, [canWebShare, shareData, markShareSuccess])

  const recipientForToken = useMemo(
    () => digitalCard?.recipients?.find(r => r.token === token),
    [digitalCard?.recipients, token],
  )

  const giftCards = useMemo<
    ReadonlyArray<DigitalCardRecipientGiftCardRunaFragment>
  >(
    () =>
      (recipientForToken?.giftCards ??
        []) as ReadonlyArray<DigitalCardRecipientGiftCardRunaFragment>,
    [recipientForToken],
  )

  const productQueries = useDigitalGiftCardProducts(
    giftCards.map(giftCard => giftCard.productCodes[0]),
  )

  const giftCardTiles: GiftCardTileProps[] = useMemo(
    () =>
      giftCards.map((gc, i) => {
        const product = productQueries[i]?.data
        return {
          amount: formatAmount(gc.amount),
          giftCardPreviewUrl: product?.image ?? undefined,
          brand: product && {
            title: product.title,
            brandImage: product.image ?? undefined,
          },
          currency: gc.currency,
        }
      }),
    [giftCards, productQueries],
  )

  return (
    <Div display="flex" width="100%" justifyContent="center">
      <Div style={{ ...backgroundImageCardStyles, width, height }} />
      <Dialog
        isOpen
        onClose={() => {}}
        sheetBgColorOverride="#fff"
        mobileOpenHeight={!isCreatedStatus ? '20vh' : ''}
        overflow="unset"
        titleBarReplacement={null}
        mobileBreakPointOverride={760}
        wrapperStyle={{ padding: isMobile ? '16px' : '0' }}
        outerWrapperStyle={{
          padding: isMobile ? '20px' : '40px',
          width: isMobile ? '100%' : '680px',
        }}
        shouldHideBlur={isCreatedStatus}
      >
        <AnimatePresence>
          {hasCopyAffordance && <LinkCopiedToaster />}
        </AnimatePresence>
        <Flex
          flexDirection="column"
          rowGap="x2"
          columnGap="x2"
          alignItems="center"
          position="relative"
          justifyContent="center"
        >
          <Flex
            flexDirection="column"
            alignItems="center"
            rowGap={isMobile ? 'x1' : 'x2'}
            width="100%"
            maxWidth={isMobile ? '100%' : '560px'}
            inset={{ horizontal: 'x2', top: 'x2' }}
          >
            <Text
              type="body"
              alignment="center"
              content={headerTitle}
              whiteSpace="normal"
              outset={!isCreatedStatus && isMobile ? { top: '70px' } : {}}
              style={{
                fontFamily,
                color: '#252525',
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: 700,
              }}
            />
            {giftCardTiles.length > 0 && (
              <Flex
                flexDirection="column"
                alignItems="center"
                rowGap={isMobile ? 'x1' : 'x2'}
                justifyContent="center"
                maxWidth={isMobile ? '100%' : '560px'}
                width="100%"
                style={{
                  marginTop: isMobile ? '8px' : '12px',
                  gap: isMobile ? '12px' : '16px',
                }}
              >
                {giftCardTiles.map((t, i) => (
                  <GiftCardTile
                    key={`${t.brand?.title ?? 'gc'}-${t.amount}-${i} currency-${t.currency}`}
                    {...t}
                  />
                ))}
              </Flex>
            )}
          </Flex>
          <Flex
            flexDirection="column"
            alignItems="center"
            width="100%"
            rowGap="x2"
          >
            <Text
              type="body"
              alignment="center"
              lineHeight={1.5}
              style={{
                fontFamily,
                color: '#6B7280',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: 600,
              }}
            >
              {headerSubtitle}
            </Text>
            <Flex
              flexDirection="row"
              columnGap="x1_5"
              alignItems="center"
              justifyContent="center"
              width="100%"
            >
              <Flex
                width="100%"
                justifyContent="center"
                alignItems="center"
                maxWidth={isMobile ? '100%' : '560px'}
                columnGap="x2"
              >
                <Div
                  onClick={canWebShare ? handleShare : undefined}
                  style={{
                    ...actionStyles,
                    flex: 1,
                    color: canWebShare ? '#444' : '#9CA3AF',
                    backgroundColor: '#fff',
                    opacity: canWebShare ? 1 : 0.6,
                    pointerEvents: canWebShare ? 'auto' : 'none',
                  }}
                  aria-disabled={!canWebShare}
                  aria-label="Send ACT via device share"
                >
                  <Icon
                    size={20}
                    name="send_two"
                    color={canWebShare ? '#444' : '#9CA3AF'}
                  />
                  {isFinalStep
                    ? ''
                    : giftCard
                      ? marketingContent?.shareScreenWGiftCardAction.content
                      : marketingContent?.shareScreenNonGiftCardAction.content}
                </Div>
                <Div
                  onClick={handleCopyToClipboard}
                  style={{
                    ...actionStyles,
                    flex: 1,
                    color: '#444',
                    backgroundColor: '#fff',
                    opacity: hasCopyAffordance ? 0.7 : 1,
                    pointerEvents: hasCopyAffordance ? 'none' : 'auto',
                  }}
                  aria-label="Copy ACT link"
                >
                  <Icon size={20} name="copy_link" color="#444" />
                  {hasCopyAffordance
                    ? 'Copied'
                    : isMobile && isFinalStep
                      ? ''
                      : 'Copy'}
                </Div>
              </Flex>
            </Flex>
            <Flex
              flexDirection="column"
              width="100%"
              justifyContent="center"
              alignItems="center"
              rowGap="x1_5"
            >
              {(isFinalStep || lastAction === 'copy') &&
                !isCreatedStatus &&
                !isSparse && (
                  <button
                    onClick={onFinish}
                    style={{
                      ...actionStyles,
                      width: '100%',
                      color: '#fff',
                      backgroundColor: '#444',
                      maxWidth: isMobile ? '100%' : '560px',
                    }}
                  >
                    Finished
                  </button>
                )}
              {isSparse && (
                <Flex
                  width="100%"
                  flexDirection="column"
                  alignItems="center"
                  outset={{ top: '8px' }}
                >
                  <Div
                    style={{
                      width: '100%',
                      height: '1px',
                      backgroundColor: '#E5E7EB',
                      margin: '16px 0',
                    }}
                  />
                  <Flex outset={{ top: '16px' }} width="100%">
                    <ThreeDayTrialModal onFinish={onFinish} />
                  </Flex>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Dialog>
    </Div>
  )
}
